/**
 * Downscale receipt photos before upload (SEE-1424).
 *
 * Phone cameras produce 1.4–3.2 MB images; on a weak job-site connection that
 * upload window (~14 s at 1 Mbps) is where dropped connections kill the save.
 * Resizing to a ~1600 px long edge yields a few hundred KB — a 5–10× shorter
 * exposure window — while staying fully legible for review, OCR and ERP
 * attachment. Runs in the browser, so it covers the native camera, the native
 * photo library and plain web uploads without an app release.
 *
 * Fails safe: any decode/encode problem returns the original file untouched.
 */

const DEFAULT_MAX_EDGE_PX = 1600;
const DEFAULT_JPEG_QUALITY = 0.85;
// Below this size the resize can't buy much; skip the work (and the re-encode).
const MIN_BYTES_TO_BOTHER = 400 * 1024;

const RESIZABLE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic']);

export interface DownscaleOptions {
  maxEdgePx?: number;
  quality?: number;
}

const decode = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof createImageBitmap === 'function') {
    try {
      // Camera photos store landscape pixels plus an EXIF Orientation tag; canvas
      // re-encoding discards EXIF, so the rotation must be baked into the pixels
      // here or portrait receipts come out sideways. 'from-image' makes that
      // explicit instead of relying on the engine default (which varies). Cast:
      // this TS lib's ImageBitmapOptions predates the 'from-image' value — and on
      // engines whose ImageOrientation enum also predates it, WebIDL argument
      // conversion THROWS on the unknown value, so this call can fail before any
      // decode happens.
      return await createImageBitmap(file, {
        imageOrientation: 'from-image',
      } as unknown as ImageBitmapOptions);
    } catch {
      // Older engines reject the 'from-image' enum value — fall through to the
      // <img> path below, which applies EXIF orientation via the CSS default
      // (image-orientation: from-image), so the downscale still happens instead
      // of silently reverting to full-size uploads.
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image decode failed'));
    };
    img.src = url;
  });
};

export const replaceExtension = (name: string, ext: string): string => {
  const dot = name.lastIndexOf('.');
  return `${dot > 0 ? name.slice(0, dot) : name}.${ext}`;
};

/** Pure scale math: fit within maxEdgePx on the long edge, never upscale. */
export const computeTargetDimensions = (
  width: number,
  height: number,
  maxEdgePx: number
): { width: number; height: number } => {
  const longEdge = Math.max(width, height);
  const scale = longEdge > 0 ? Math.min(1, maxEdgePx / longEdge) : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

export const isDownscalableImage = (file: File): boolean => RESIZABLE_TYPES.has(file.type.toLowerCase());

/**
 * Returns a smaller JPEG `File` when the image is large enough to matter,
 * otherwise the original `File`. Never throws.
 */
export const downscaleReceiptImage = async (
  file: File,
  { maxEdgePx = DEFAULT_MAX_EDGE_PX, quality = DEFAULT_JPEG_QUALITY }: DownscaleOptions = {}
): Promise<File> => {
  if (!isDownscalableImage(file) || file.size < MIN_BYTES_TO_BOTHER) {
    return file;
  }

  try {
    const source = await decode(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    const { width: targetW, height: targetH } = computeTargetDimensions(width, height, maxEdgePx);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // White backing so PNG transparency doesn't turn black in JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(source, 0, 0, targetW, targetH);
    if ('close' in source && typeof source.close === 'function') source.close();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    // Only swap in the result when it's actually smaller — a tiny already-JPEG
    // image can re-encode larger.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], replaceExtension(file.name, 'jpg'), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};
